import {
  DeleteOutline,
  EditOutlined,
  OpenInNew,
} from "@mui/icons-material";
import {
  Box,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import dayjs from "dayjs";

import { PatientSupportingDocumentRecord } from "./types";

interface PatientSupportingDocumentListProps {
  documents: PatientSupportingDocumentRecord[];
  accent: string;
  pageLayout: boolean;
  onView: (document: PatientSupportingDocumentRecord) => void;
  onReplace: (document: PatientSupportingDocumentRecord) => void;
  onRemove: (document: PatientSupportingDocumentRecord) => void;
}

/** Lista compacta que mantém as ações associadas ao documento correto. */
export function PatientSupportingDocumentList({
  documents,
  accent,
  pageLayout,
  onView,
  onReplace,
  onRemove,
}: PatientSupportingDocumentListProps) {
  if (!documents.length) {
    return (
      <Box
        sx={{
          minHeight: pageLayout ? 72 : 80,
          display: "flex",
          alignItems: "center",
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Nenhum arquivo enviado ainda.
        </Typography>
      </Box>
    );
  }

  return (
    <Stack
      spacing={0.75}
      sx={{
        maxHeight: pageLayout ? 210 : 240,
        overflowY: "auto",
        pr: 0.25,
      }}
    >
      {documents.map((document) => (
        <Stack
          key={
            document.documentReferenceId ??
            `${document.fileName}-${document.updatedAt}`
          }
          direction="row"
          alignItems="center"
          spacing={0.5}
          sx={{
            px: 1,
            py: 0.75,
            borderRadius: 1.5,
            bgcolor: alpha("#808080", 0.06),
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} noWrap>
              {document.description || document.fileName}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              noWrap
            >
              {document.description ? `${document.fileName} • ` : ""}
              {dayjs(document.updatedAt).format("DD/MM/YYYY [às] HH:mm")}
            </Typography>
          </Box>

          <Tooltip title="Abrir documento">
            <IconButton
              size="small"
              aria-label={`Abrir ${document.fileName}`}
              onClick={() => onView(document)}
              sx={{ color: accent }}
            >
              <OpenInNew fontSize="small" />
            </IconButton>
          </Tooltip>
       
          <Tooltip title="Remover documento">
            <IconButton
              size="small"
              aria-label={`Remover ${document.fileName}`}
              onClick={() => onRemove(document)}
              sx={{ color: "error700.main" }}
            >
              <DeleteOutline fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ))}
    </Stack>
  );
}
